/**
 * PostHog Service - Singleton service for managing PostHog analytics
 * Note: This system tracks dental professional scheduling - no patient data
 */

import posthog, { PostHog } from 'posthog-js';
import { PosthogConfig, PosthogEventProperties } from './types';
import { sanitizePayload } from '~/utils/posthog/sanitizers';
import history from '../../history';

class PosthogService {
  private static instance: PosthogService;
  private posthogInstance: PostHog | null = null;
  private initialized: boolean = false;
  private flagsReady: boolean = false;
  private flagsReadyPromise: Promise<void> | null = null;
  private flagsReadyResolver: (() => void) | null = null;
  private config: PosthogConfig | null = null;

  private constructor() {}

  /**
   * Get singleton instance of PosthogService
   */
  static getInstance(): PosthogService {
    if (!PosthogService.instance) {
      PosthogService.instance = new PosthogService();
    }
    return PosthogService.instance;
  }

  /**
   * Get the underlying PostHog instance (for internal use)
   */
  getPosthogInstance(): PostHog | null {
    return this.posthogInstance;
  }

  /**
   * Initialize PostHog with configuration
   */
  initialize(config: PosthogConfig): void {
    // Don't initialize if already initialized or disabled
    if (this.initialized || config.disabled || !config.apiKey) {
      if (config.debug) {
        console.log('[PostHog] Initialization skipped', {
          initialized: this.initialized,
          disabled: config.disabled,
          hasApiKey: !!config.apiKey,
        });
      }
      return;
    }

    this.config = config;

    try {
      posthog.init(config.apiKey, {
        api_host: config.apiHost || 'https://us.i.posthog.com',
        autocapture: false, // We'll track manually via saga
        capture_pageview: false, // Manual page view tracking for better control
        capture_pageleave: false, // Disable automatic page leave tracking
        disable_session_recording: true, // Disable automatic recording - opt-in only for specific flows
        debug: config.debug,
        persistence: 'localStorage+cookie', // Better for cross-subdomain tracking
        cross_subdomain_cookie: true, // Enable cross-subdomain tracking
        // Note: config.js 404 is expected until you create feature flags/experiments in PostHog
      });

      posthog.register({
        environment: process.env.REACT_APP_TEMP_MEE_ENV || 'development',
      });

      // Set as person property for feature flag evaluation (works for anonymous users too)
      posthog.people.set({
        environment: process.env.REACT_APP_TEMP_MEE_ENV || 'development',
      });

      // Use the global posthog instance directly since init is synchronous
      this.posthogInstance = posthog;
      // Also expose globally for toolbar
      (window as any).posthog = posthog;
      this.initialized = true;

      // Set up promise for feature flags readiness
      this.flagsReadyPromise = new Promise<void>((resolve) => {
        this.flagsReadyResolver = resolve;
      });

      // Set up feature flags ready listener
      this.posthogInstance.onFeatureFlags(() => {
        this.flagsReady = true;
        if (this.flagsReadyResolver) {
          this.flagsReadyResolver();
          this.flagsReadyResolver = null; // Clear resolver after use
        }
        if (config.debug) {
          console.log('[PostHog] Feature flags loaded and ready');
        }
      });

      // Set up history listener for pageview tracking
      this.setupPageViewTracking();
    } catch (error) {
      console.error('[PostHog] Initialization error:', error);
    }
  }

  /**
   * Set up automatic pageview tracking using history
   */
  private setupPageViewTracking(): void {
    // Track initial page view
    this.trackPageView(window.location.pathname, {
      search: window.location.search,
      hash: window.location.hash,
    });

    // Listen for route changes
    history.listen((update) => {
      const pathname = update.location ? update.location.pathname : window.location.pathname;
      const search = update.location ? update.location.search : window.location.search;
      const hash = update.location ? update.location.hash : window.location.hash;

      this.trackPageView(pathname, {
        search,
        hash,
      });
    });
  }

  /**
   * Identify a user with PostHog
   */
  identify(userId: string, traits?: Record<string, any>): void {
    if (!this.isReady()) return;

    try {
      // Only sanitize auth tokens, preserve all user data
      const sanitizedTraits = sanitizePayload(traits || {});
      this.posthogInstance?.identify(userId, sanitizedTraits);

      if (this.config?.debug) {
        console.log('[PostHog] User identified:', userId, sanitizedTraits);
      }
    } catch (error) {
      console.error('[PostHog] Identify error:', error);
    }
  }

  /**
   * Track an event with PostHog
   */
  track(event: string, properties?: PosthogEventProperties): void {
    if (!this.isReady()) return;

    try {
      // Only sanitize auth tokens, preserve all business data
      const sanitizedProperties = sanitizePayload(properties || {});
      this.posthogInstance?.capture(event, sanitizedProperties);

      if (this.config?.debug) {
        console.log('[PostHog] Event tracked:', event, sanitizedProperties);
      }
    } catch (error) {
      console.error('[PostHog] Track error:', error);
    }
  }

  /**
   * Track a page view
   */
  trackPageView(path: string, properties?: PosthogEventProperties): void {
    if (!this.isReady()) return;

    try {
      const sanitizedProperties = sanitizePayload({
        ...properties,
        path,
        url: window.location.href,
        referrer: document.referrer,
      });

      this.posthogInstance?.capture('$pageview', sanitizedProperties);

      if (this.config?.debug) {
        console.log('[PostHog] Page view tracked:', path, sanitizedProperties);
      }
    } catch (error) {
      console.error('[PostHog] Page view error:', error);
    }
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.isReady()) return;

    try {
      const sanitizedProperties = sanitizePayload(properties);
      this.posthogInstance?.people?.set(sanitizedProperties);

      if (this.config?.debug) {
        console.log('[PostHog] User properties set:', sanitizedProperties);
      }
    } catch (error) {
      console.error('[PostHog] Set user properties error:', error);
    }
  }

  /**
   * Reset user (on logout)
   */
  reset(): void {
    if (!this.isReady()) return;

    try {
      this.posthogInstance?.reset();

      if (this.config?.debug) {
        console.log('[PostHog] User reset');
      }
    } catch (error) {
      console.error('[PostHog] Reset error:', error);
    }
  }

  /**
   * Start session recording (opt-in for specific flows)
   */
  startSessionRecording(): void {
    if (!this.isReady()) return;

    try {
      this.posthogInstance?.startSessionRecording();

      if (this.config?.debug) {
        console.log('[PostHog] Session recording started');
      }
    } catch (error) {
      console.error('[PostHog] Start session recording error:', error);
    }
  }

  /**
   * Stop session recording
   */
  stopSessionRecording(): void {
    if (!this.isReady()) return;

    try {
      this.posthogInstance?.stopSessionRecording();

      if (this.config?.debug) {
        console.log('[PostHog] Session recording stopped');
      }
    } catch (error) {
      console.error('[PostHog] Stop session recording error:', error);
    }
  }

  /**
   * Check if session recording is active
   */
  isSessionRecordingEnabled(): boolean {
    if (!this.isReady()) return false;

    try {
      return this.posthogInstance?.sessionRecordingStarted() || false;
    } catch (error) {
      console.error('[PostHog] Check session recording error:', error);
      return false;
    }
  }

  /**
   * Get distinct ID of current user
   */
  getDistinctId(): string | undefined {
    if (!this.isReady()) return undefined;
    return this.posthogInstance?.get_distinct_id();
  }

  /**
   * Check if a feature flag is enabled
   */
  isFeatureEnabled(flagKey: string, defaultValue: boolean = false): boolean {
    if (!this.isReady()) return defaultValue;

    try {
      const isEnabled = this.posthogInstance?.isFeatureEnabled(flagKey);

      if (this.config?.debug) {
        console.log(`[PostHog] Feature flag '${flagKey}':`, isEnabled);
      }

      return isEnabled ?? defaultValue;
    } catch (error) {
      console.error('[PostHog] Feature flag error:', error);
      return defaultValue;
    }
  }

  /**
   * Get feature flag variant value
   * Returns the variant string (e.g., 'control', 'test') or boolean if no variants
   */
  getFeatureFlagVariant(flagKey: string): string | boolean | null {
    if (!this.isReady()) return null;

    try {
      // getFeatureFlag returns the variant value when using multivariate flags
      return this.posthogInstance?.getFeatureFlag(flagKey) ?? null;
    } catch (error) {
      console.error('[PostHog] Feature flag variant error:', error);
      return null;
    }
  }

  /**
   * Check if a feature flag has a specific variant
   */
  isFeatureFlagVariant(flagKey: string, expectedVariant: string): boolean {
    const variant = this.getFeatureFlagVariant(flagKey);
    return variant === expectedVariant;
  }

  /**
   * Get feature flag payload
   */
  getFeatureFlagPayload(flagKey: string): any {
    if (!this.isReady()) return null;

    try {
      return this.posthogInstance?.getFeatureFlagPayload(flagKey);
    } catch (error) {
      console.error('[PostHog] Feature flag payload error:', error);
      return null;
    }
  }

  /**
   * Reload feature flags from server
   */
  reloadFeatureFlags(): void {
    if (!this.isReady()) return;

    try {
      this.posthogInstance?.reloadFeatureFlags();

      if (this.config?.debug) {
        console.log('[PostHog] Feature flags reloaded');
      }
    } catch (error) {
      console.error('[PostHog] Reload feature flags error:', error);
    }
  }

  /**
   * Register a callback for when feature flags are loaded
   */
  onFeatureFlags(
    callback: (flags: string[], variants: Record<string, string | boolean>) => void,
  ): void {
    if (!this.isReady()) return;

    try {
      this.posthogInstance?.onFeatureFlags(callback);
    } catch (error) {
      console.error('[PostHog] Feature flags callback error:', error);
    }
  }

  /**
   * Check if PostHog is ready to use
   */
  private isReady(): boolean {
    const ready = this.initialized && this.posthogInstance !== null && !this.config?.disabled;

    if (!ready && this.config?.debug) {
      console.log('[PostHog] Not ready:', {
        initialized: this.initialized,
        hasInstance: this.posthogInstance !== null,
        disabled: this.config?.disabled,
      });
    }

    return ready;
  }

  /**
   * Check if feature flags have been loaded (synchronous check)
   * Returns true if flags are ready, false otherwise
   */
  areFlagsReady(): boolean {
    return this.flagsReady && this.isReady();
  }

  /**
   * Wait for feature flags to be loaded from the server
   * Use this when you need to ensure flags are loaded before checking them
   * to avoid race conditions with default values
   *
   * NOTE: For React components, use the useFeatureFlag hook instead,
   * which handles flag readiness internally.
   *
   * @example
   * // In a saga
   * const posthog = PosthogService.getInstance();
   * yield call([posthog, posthog.waitForFlags]);
   * const isEnabled = posthog.isFeatureEnabled('my-feature');
   *
   * @example
   * // In a thunk or utility function
   * await PosthogService.getInstance().waitForFlags();
   * const isEnabled = PosthogService.getInstance().isFeatureEnabled('my-feature');
   */
  async waitForFlags(): Promise<void> {
    if (!this.isReady()) {
      if (this.config?.disabled) {
        return; // Service is disabled, flags won't load
      }
      throw new Error('[PostHog] Service not initialized');
    }

    if (this.flagsReady) {
      return; // Already ready
    }

    if (this.flagsReadyPromise) {
      await this.flagsReadyPromise;
    }
  }
}

export default PosthogService;
